import { aiHeaders } from '../lib/photoAi';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Sparkles, ScanLine, Check, Plus, Loader2, Undo2, Camera, ImagePlus } from 'lucide-react';
import { detectParts, mergeDetections } from '../lib/detector';
import { getPrices, setPrice, findPrice, eur, aiToken } from '../lib/license';

const PALETTE = ['#ff2d2d', '#ffd60a', '#22c55e', '#1d6fe8', '#ff6a13', '#d946ef', '#06b6d4', '#ffffff'];
const API = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/count`;

function render(cv, img, points, types, withLegend, t) {
  const ctx = cv.getContext('2d');
  ctx.drawImage(img, 0, 0, cv.width, cv.height);
  const R = Math.max(cv.width, cv.height) / 85;
  const seq = {};
  points.forEach((p) => {
    seq[p.label] = (seq[p.label] || 0) + 1;
    const x = p.x * cv.width, y = p.y * cv.height;
    ctx.beginPath(); ctx.arc(x, y, R, 0, Math.PI * 2);
    ctx.fillStyle = PALETTE[types.indexOf(p.label) % PALETTE.length]; ctx.fill();
    ctx.lineWidth = R / 4; ctx.strokeStyle = '#0e1e30'; ctx.stroke();
    ctx.fillStyle = '#0e1e30'; ctx.font = `bold ${R * 1.05}px Arial`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(String(seq[p.label]), x, y + R * 0.05);
  });
  if (!withLegend) return;
  const rows = types.filter((ty) => seq[ty]);
  const fs = R * 1.4, pad = fs * 0.6;
  ctx.font = `bold ${fs}px Arial`;
  const lines = [...rows.map((ty) => `${seq[ty]}x ${ty}`), `${t.invTotal}: ${points.length}`];
  const w = Math.max(...lines.map((l) => ctx.measureText(l).width)) + fs * 2 + pad * 2;
  ctx.fillStyle = 'rgba(14,30,48,0.85)';
  ctx.fillRect(pad, pad, w, lines.length * fs * 1.4 + pad);
  lines.forEach((l, i) => {
    const y = pad * 1.5 + i * fs * 1.4 + fs / 2;
    if (i < rows.length) { ctx.fillStyle = PALETTE[types.indexOf(rows[i]) % PALETTE.length]; ctx.beginPath(); ctx.arc(pad * 2 + fs / 2, y, fs / 2.4, 0, Math.PI * 2); ctx.fill(); }
    ctx.fillStyle = '#fff'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillText(l, pad * 2 + fs * 1.3, y);
  });
}

export default function InventoryCounter({ t, lang, photo, onApply, onClose, onNext }) {
  const camRef = useRef(null);
  const galRef = useRef(null);
  const cvRef = useRef(null);
  const imgRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [history, setHistory] = useState([]);
  const [types, setTypes] = useState([t.invDefaultType]);
  const [cur, setCur] = useState(t.invDefaultType);
  const [busy, setBusy] = useState(false);
  const [prices, setPrices] = useState(getPrices());
  const [draft, setDraft] = useState({});

  const setPts = (next) => { setHistory((h) => [...h, points]); setPoints(next); };

  const autoCount = async (img, useAi) => {
    setBusy(true);
    const blobs = detectParts(img);
    let pts = blobs.map((b) => ({ x: b.x, y: b.y, label: t.invDefaultType }));
    if (useAi && navigator.onLine) {
      try {
        const tok = await aiToken();
        if (!tok) throw new Error();
        const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-License-Token': tok, ...aiHeaders() }, body: JSON.stringify({ image_base64: photo.data, lang }) });
        if (!res.ok) throw new Error();
        const { items } = await res.json();
        if (items.length) pts = mergeDetections(items, blobs);
      } catch { toast.message(t.invAiErr); }
    } else if (useAi) toast.message(t.invAiErr);
    const ty = [...new Set(pts.map((p) => p.label))];
    setTypes((old) => [...new Set([...ty, ...old.filter((o) => o !== t.invDefaultType || ty.includes(o) || !ty.length)])]);
    setCur(ty[0] || t.invDefaultType);
    setPts(pts);
    setBusy(false);
  };

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      cvRef.current.width = img.width; cvRef.current.height = img.height;
      autoCount(img, true);
    };
    img.src = photo.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (imgRef.current) render(cvRef.current, imgRef.current, points, types, false, t); }, [points, types, t]);

  const tap = (e) => {
    if (busy) return;
    const r = cvRef.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
    const rad = (Math.max(cvRef.current.width, cvRef.current.height) / 85) * 1.3;
    const hit = points.findIndex((p) => Math.hypot((p.x - x) * cvRef.current.width, (p.y - y) * cvRef.current.height) < rad);
    setPts(hit >= 0 ? points.filter((_, i) => i !== hit) : [...points, { x, y, label: cur }]);
  };

  const addType = () => {
    const name = window.prompt(t.invPromptType);
    if (!name?.trim()) return;
    setTypes((ty) => [...new Set([...ty, name.trim()])]);
    setCur(name.trim());
  };

  const counts = types.map((ty) => [ty, points.filter((p) => p.label === ty).length]).filter(([, n]) => n);

  const priceOf = (ty) => findPrice(prices, ty);
  const priced = counts.filter(([ty]) => priceOf(ty) != null);
  const sum = priced.reduce((s, [ty, n]) => s + n * priceOf(ty), 0);
  const editPrice = (ty, v) => { setDraft((d) => ({ ...d, [ty]: v })); setPrices(setPrice(ty, v)); };

  const apply = (keep = false) => {
    render(cvRef.current, imgRef.current, points, types, true, t);
    const summary = counts.map(([ty, n]) => (priceOf(ty) != null ? `${n}x ${ty} (à ${eur(priceOf(ty), t.locale)} = ${eur(n * priceOf(ty), t.locale)})` : `${n}x ${ty}`)).join(', ')
      + (priced.length ? ` – ${t.invSum}: ${eur(sum, t.locale)}` : '');
    onApply({
      data: cvRef.current.toDataURL('image/jpeg', 0.88), w: cvRef.current.width, h: cvRef.current.height,
      caption: `${t.invCaption}: ${summary}`, inventory: Object.fromEntries(counts),
      inventoryPrices: Object.fromEntries(priced.map(([ty]) => [ty, priceOf(ty)])),
    }, `${t.invUsed}: ${summary}`, keep);
  };
  const next = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (points.length) { apply(true); toast.success(t.invSavedNext); }
    onNext(f);
  };

  return createPortal(
    <div className="editor-backdrop" data-testid="inventory-counter">
      <div className="editor inv-editor">
        <div className="editor-head">
          <h4>{t.invTitle}</h4>
          <button className="icon-btn" data-testid="inventory-close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        <p className="hint" style={{ margin: 0 }}>{t.invHint}</p>
        <div className="editor-tools">
          <span className="inv-label">{t.invType}:</span>
          {types.map((ty, i) => (
            <button key={ty} data-testid={`inventory-type-${i}`} className={`tool ${cur === ty ? 'on' : ''}`} onClick={() => setCur(ty)}>
              <i className="inv-dot" style={{ background: PALETTE[i % PALETTE.length] }} /> {ty}
              <b className="inv-n">{points.filter((p) => p.label === ty).length}</b>
            </button>
          ))}
          <button className="tool" data-testid="inventory-add-type-btn" onClick={addType}><Plus size={14} /> {t.invNewType}</button>
        </div>
        <div className="editor-canvas-wrap inv-wrap">
          <canvas ref={cvRef} data-testid="inventory-canvas" onPointerDown={tap} />
          {busy && <div className="inv-busy" data-testid="inventory-busy"><Loader2 className="spin" /> {t.invRunning}</div>}
        </div>
        {counts.length > 0 && (
          <div className="inv-prices" data-testid="inventory-prices">
            {counts.map(([ty, n], i) => (
              <div className="inv-price-row" key={ty} data-testid={`inventory-price-row-${i}`}>
                <span><b>{n}x</b> {ty}</span>
                <label>{t.invPrice}
                  <input type="text" inputMode="decimal" placeholder="0,00" data-testid={`inventory-price-input-${i}`} value={draft[ty] ?? (priceOf(ty) != null ? String(priceOf(ty)).replace('.', t.locale === 'en-GB' || t.locale === 'en-US' ? '.' : ',') : '')} onChange={(e) => editPrice(ty, e.target.value)} />
                </label>
                <span className="inv-amount" data-testid={`inventory-amount-${i}`}>{priceOf(ty) != null ? eur(n * priceOf(ty), t.locale) : '–'}</span>
              </div>
            ))}
            <div className="inv-price-row inv-sum"><span>{t.invSum}</span><span /><b data-testid="inventory-sum">{eur(sum, t.locale)}</b></div>
          </div>
        )}
        <div className="row inv-actions">
          <span className="draw-count" data-testid="inventory-total">{t.invTotal}: {points.length}</span>
          <button className="btn btn-outline" data-testid="inventory-ai-btn" disabled={busy} onClick={() => autoCount(imgRef.current, true)}><Sparkles size={16} /> {t.invAuto}</button>
          <button className="btn btn-outline" data-testid="inventory-local-btn" disabled={busy} onClick={() => autoCount(imgRef.current, false)}><ScanLine size={16} /> {t.invLocal}</button>
          <button className="btn btn-outline" data-testid="inventory-undo-btn" disabled={!history.length} onClick={() => { setPoints(history[history.length - 1]); setHistory((h) => h.slice(0, -1)); }}><Undo2 size={16} /> {t.edUndo}</button>
          <button className="btn btn-green" data-testid="inventory-apply-btn" disabled={busy || !points.length} onClick={() => apply()}><Check size={16} /> {t.invApply}</button>
          <div className="inv-next">
            <button className="btn btn-proj" data-testid="inventory-next-camera-btn" disabled={busy} onClick={() => camRef.current.click()}><Camera size={16} /> {t.invNextPhoto}</button>
            <button className="btn btn-outline" data-testid="inventory-next-gallery-btn" disabled={busy} onClick={() => galRef.current.click()}><ImagePlus size={16} /> {t.invNextGallery}</button>
          </div>
          <input ref={camRef} type="file" accept="image/*" capture="environment" hidden data-testid="inventory-next-camera-input" onChange={next} />
          <input ref={galRef} type="file" accept="image/*" hidden data-testid="inventory-next-gallery-input" onChange={next} />
        </div>
      </div>
    </div>,
    document.body,
  );
}
